/**
 * The built-in hail interaction (M3 plan R12, §2.9 flow 2): within `hail.rangeM` of a station's hull (scale build
 * SC-5a2b: `hailDistance`, the bounding sphere; the prompt's `distance` is that hull distance, floored at 0, so the
 * arbiter's nearest-prompt pick sees a km-scale station as near as its hull), the HUD shows
 * "[F] Hail ⟨name⟩"; above `hail.maxSpeed` the prompt is blocked with "Slow below ⟨n⟩ m/s". Activating it asks the UI
 * for the station services panel (`uiRequest = { kind: 'services', stationId }`). `nearest` is allocation-free.
 */
import type { Sim } from '../../sim'
import { hailDistance } from '../session/hailRange'
import { Life, Pos64, Vel } from '../../world/components'
import type { InteractableProvider, InteractionPrompt, ReachCore } from '../runtime/types'
import type { ReachCommandResult } from '../types'

/** What the hail reads from the session config (the live object, so the dev panel can tune it). */
export interface HailConfig {
  rangeM: number
  maxSpeed: number
}

/** The blocked-prompt text for a speed limit. */
export function slowBelowText(maxSpeed: number): string {
  return `Slow below ${Math.round(maxSpeed)} m/s`
}

export function createHailProvider(cfg: HailConfig): InteractableProvider {
  let blockedFor = cfg.maxSpeed
  let blocked = slowBelowText(cfg.maxSpeed)
  return {
    nearest(core: ReachCore, sim: Sim, out: InteractionPrompt): boolean {
      const session = core.session
      const layout = session.layout
      const p = sim.playerEid
      if (layout === null || !session.ready || p < 0 || Life.alive[p] !== 1 || core.travel.mode !== 'normal') return false
      const px = Pos64.x[p]
      const py = Pos64.y[p]
      const pz = Pos64.z[p]
      let best = -1
      let bestD = cfg.rangeM
      const stations = layout.stations
      for (let i = 0; i < stations.length; i++) {
        const d = hailDistance(stations[i], px, py, pz)
        if (d <= bestD) {
          bestD = d
          best = i
        }
      }
      if (best < 0) return false
      const speed = Math.sqrt(Vel.x[p] * Vel.x[p] + Vel.y[p] * Vel.y[p] + Vel.z[p] * Vel.z[p])
      if (blockedFor !== cfg.maxSpeed) {
        // Rare: the dev panel changed the limit.
        blockedFor = cfg.maxSpeed
        blocked = slowBelowText(cfg.maxSpeed)
      }
      out.kind = 'hail'
      out.index = stations[best].index
      out.distance = Math.max(0, bestD)
      out.blocked = speed > cfg.maxSpeed ? blocked : null
      return true
    },

    activate(core: ReachCore, _sim: Sim, prompt: InteractionPrompt): ReachCommandResult {
      const station = core.session.layout?.stations[prompt.index]
      if (station === undefined) return { ok: false, error: 'invalid', message: 'No station to hail' }
      if (prompt.blocked !== null) return { ok: false, error: 'outOfRange', message: prompt.blocked }
      core.uiRequest = { kind: 'services', stationId: station.id }
      return { ok: true, message: `Hailing ${station.name}` }
    },
  }
}
